import { Container } from "@/screens/rewards/components/Container"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import {
  FieldWithLabel,
  TextFieldWithLabel,
} from "@/components/forms/FieldWithLabel"
import { Masks } from "react-native-mask-input"
import { isAppleHiddenEmail, isValidEmail } from "@/utils/StringUtils"
import { useState } from "react"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { View } from "react-native"
import BFDropDown from "@/components/standard/BFDropDown"
import { AccordionHeader } from "@/screens/loans/components/setup/AccordionHeader"
import {
  AccordionContent,
  AccordionItem,
} from "@/components/reusables/ui/accordion"
import { Body } from "@/components/standard/BFText"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useUser } from "@/queries/user"

export interface ContactInfo {
  firstName: string
  lastName: string
  phoneNumber: string
  contactEmail: string

  borrower?: string
  borrowerName?: string
}

export const BorrowerAndContactQuestion = ({
  updateContact,
}: {
  updateContact: () => void
}) => {
  const { loanApplication, update, save } = useLoanContext()

  const { data: userData } = useUser()
  const {
    firstName: userFirstName,
    lastName: userLastName,
    phoneNumber: userPhoneNumber,
  } = userData?.me || {}

  // We don't want to populate if an obscured Apple email is the user's login
  const displayContactEmail: string | undefined =
    userData?.me?.contactEmail ||
    (isAppleHiddenEmail(userData?.me?.email) ? "" : userData?.me?.email)

  const [firstName, setFirstName] = useState<string>(userFirstName || "")
  const [lastName, setLastName] = useState<string>(userLastName || "")
  const [phoneNumber, setPhoneNumber] = useState<string>(userPhoneNumber || "")
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>()
  const [contactEmail, setContactEmail] = useState<string>(
    displayContactEmail || ""
  )
  const [contactEmailError, setContactEmailError] = useState<
    string | undefined
  >()
  const [loading, setLoading] = useState(false)

  const notValidContactInfo: boolean =
    !!contactEmailError || !!phoneNumberError || !firstName || !lastName

  const borrowerName = loanApplication.borrowerNames?.[0] || ""

  const saveShouldBeDisabled: boolean =
    !loanApplication.borrower ||
    (loanApplication.borrower === "entity" && borrowerName.length < 3) ||
    notValidContactInfo

  const onPressSave = () => {
    if (phoneNumberError || contactEmailError) {
      return
    }

    const digits = phoneNumber.replace(/\D/g, "")
    update({
      firstName,
      lastName,
      contactEmail,
      phoneNumber: digits,
    })
    setLoading(true)
    void (async () => {
      try {
        await save()
        // If we can't save need to call this at a delay so the update()
        // has taken place.
        setTimeout(() => {
          updateContact()
        }, 0)
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <AccordionItem value={"borrower"}>
      <AccordionHeader
        text={"Borrower"}
        value={
          loanApplication.borrower
            ? loanApplication.borrower === "self"
              ? `${firstName} ${lastName}`
              : loanApplication.borrowerNames?.[0] || "Entity"
            : ""
        }
      />
      <AccordionContent>
        <Body>Verify the borrower&apos;s contact details</Body>
        <Container>
          <Vertical className={"gap-3"}>
            <FieldWithLabel
              label={"Borrowing As"}
              required={true}
              showRequiredIndicator={true}
            >
              <BFDropDown
                fullWidth={true}
                options={[
                  { label: "Self", value: "self" },
                  { label: "Entity", value: "entity" },
                ]}
                value={loanApplication.borrower || null}
                onValueChange={(value) => {
                  if (value) {
                    update({ borrower: value })
                  }
                }}
                zIndex={10}
              />
            </FieldWithLabel>

            <TextFieldWithLabel
              label={"First Name"}
              initialValue={firstName}
              required={true}
              onValueChange={(value) => {
                setFirstName(value)
              }}
            />

            <TextFieldWithLabel
              label={"Last Name"}
              initialValue={lastName}
              required={true}
              onValueChange={(value) => {
                setLastName(value)
              }}
            />

            <TextFieldWithLabel
              label={"Phone Number"}
              initialValue={phoneNumber}
              required={true}
              validator={(value) =>
                value.length !== 10 ? "Invalid phone number" : undefined
              }
              onValueChange={(value, error) => {
                setPhoneNumber(value)
                setPhoneNumberError(error)
              }}
              mask={Masks.USA_PHONE}
              textFieldProps={{
                placeholder: "(___) ___-____",
                keyboardType: "numeric",
              }}
            />

            <TextFieldWithLabel
              label={"Email"}
              initialValue={contactEmail}
              required={true}
              validator={(value) =>
                isValidEmail(value) ? undefined : "Not a valid email address"
              }
              onValueChange={(value, error) => {
                setContactEmail(value)
                setContactEmailError(error)
              }}
            />

            {loanApplication.borrower === "entity" && (
              <TextFieldWithLabel
                label={"Entity Name"}
                initialValue={loanApplication.borrowerNames?.[0] || ""}
                required={true}
                onValueChange={(value) => {
                  update({ borrowerNames: [value] })
                }}
              />
            )}

            <Horizontal className={"justify-between"}>
              <View></View>
              <PrimaryButton
                onPress={onPressSave}
                loading={loading}
                size={"sm"}
                disabled={saveShouldBeDisabled}
                title={"Continue"}
                className={"max-w-[150px]"}
                testID={"save-contact-info"}
              />
            </Horizontal>
          </Vertical>
        </Container>
      </AccordionContent>
    </AccordionItem>
  )
}
